import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { StatusChip } from './StatusChip';
import { useIsMobile } from 'hooks/useIsMobile';
import { fetchBulkEmailRecipients } from 'api/emailHistory/bulkEmailsApi';
import { getListContacts } from 'api/canmail/CANmailAPI';

/**
 * @component RecipientsModal
 * @description Modal displaying contact list for a bulk email or campaign/mailing list
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {function} props.onClose - Close handler
 * @param {number} [props.bulkEmailId] - Bulk email ID
 * @param {number} [props.listId] - List ID (for campaigns/mailing lists)
 * @param {boolean} [props.readOnly=true] - Whether the list is read-only
 * @returns {JSX.Element}
 */
export function RecipientsModal({ open, onClose, bulkEmailId, listId, readOnly = true }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [recipients, setRecipients] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (bulkEmailId) {
        // Load recipients from bulk email
        const data = await fetchBulkEmailRecipients(bulkEmailId, {
          page,
          pageSize,
        });
        setRecipients(data.rows || []);
        setTotalRows(data.totalRows || 0);
      } else if (listId) {
        // Load contacts from mailing list/campaign
        // Note: getListContacts doesn't support pagination, so we'll fetch all and paginate client-side
        const LIST_TYPE_ID = 1; // Standard mailing list type
        const allContacts = await getListContacts(listId, LIST_TYPE_ID);
        
        if (!Array.isArray(allContacts)) {
          setRecipients([]);
          setTotalRows(0);
          return;
        }

        // Transform contacts to match recipient format
        // API returns: id, email_address, first_name, last_name
        const transformedContacts = allContacts.map((contact) => {
          // Handle both snake_case (API) and camelCase (legacy) formats
          const contactId = contact.id || contact.contactID || contact.ContactID;
          const firstName = contact.first_name || contact.firstName || '';
          const lastName = contact.last_name || contact.lastName || '';
          const email = contact.email_address || contact.email || contact.Email || '';
          
          // Build contact name
          let contactName = '';
          if (firstName && lastName) {
            contactName = `${firstName} ${lastName}`.trim();
          } else if (firstName) {
            contactName = firstName;
          } else if (lastName) {
            contactName = lastName;
          } else if (contact.name) {
            contactName = contact.name;
          } else {
            contactName = 'Unknown';
          }
          
          return {
            individualEmailId: contactId,
            contactName,
            emailAddress: email,
            status: 'N/A', // Campaign contacts don't have email status
            openCount: 0,
            clickCount: 0,
          };
        });

        // Client-side pagination
        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        setRecipients(transformedContacts.slice(startIndex, endIndex));
        setTotalRows(transformedContacts.length);
      }
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setError(err.message || 'Failed to load recipients');
      setRecipients([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [bulkEmailId, listId, page, pageSize]);

  useEffect(() => {
    if (open && (bulkEmailId || listId)) {
      loadRecipients();
    }
  }, [open, bulkEmailId, listId, loadRecipients]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        Recipients
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isMobile ? (
          <Box>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : recipients.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No recipients found
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {recipients.map((recipient) => {
                  const contactId = recipient.contactId || recipient.individualEmailId;
                  return (
                    <Card
                      key={recipient.individualEmailId}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => {
                        navigate(`/contacts?contactId=${contactId}`);
                        onClose();
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                          {recipient.contactName || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: bulkEmailId ? 1 : 0 }}>
                          {recipient.emailAddress}
                        </Typography>
                        {bulkEmailId && (
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Status
                              </Typography>
                              <StatusChip status={recipient.status} />
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Opens
                              </Typography>
                              <Typography variant="body2" fontWeight="500">
                                {recipient.openCount || 0}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Clicks
                              </Typography>
                              <Typography variant="body2" fontWeight="500">
                                {recipient.clickCount || 0}
                              </Typography>
                            </Box>
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    {bulkEmailId && (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Opens</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Clicks</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={bulkEmailId ? 5 : 2} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : recipients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={bulkEmailId ? 5 : 2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No recipients found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recipients.map((recipient) => {
                      const contactId = recipient.contactId || recipient.individualEmailId;
                      return (
                        <TableRow 
                          key={recipient.individualEmailId} 
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/contacts?contactId=${contactId}`)}
                        >
                          <TableCell>
                            {recipient.contactName || 'Unknown'}
                          </TableCell>
                          <TableCell>{recipient.emailAddress}</TableCell>
                          {bulkEmailId && (
                            <>
                              <TableCell>
                                <StatusChip status={recipient.status} />
                              </TableCell>
                              <TableCell align="right">{recipient.openCount || 0}</TableCell>
                              <TableCell align="right">{recipient.clickCount || 0}</TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

